// The in-person signing panel (Grant, 2026-08-24 — audit item).
//
// CONSUMER: internal-ui.
//
// Shown instead of the ordinary "Sent for signature" confirmation when
// the advisor chose "Sign here now": the envelope exists, nobody was
// emailed, and each signer's link is rendered as a QR code for them to
// scan with THEIR OWN phone. The advisor watches the rows flip to
// "Signed" without leaving the screen.
//
// DECISION — why the client's own phone, not the advisor's laptop.
// "In person signing" on other platforms usually means handing over the
// sender's device. Here that device is signed into an OS holding PII
// for ~19,000 people, and the signing page shares its cookie jar, so a
// client left alone with it is one typed URL away from any household in
// the firm. A QR onto their own phone has no such session and needs no
// trust. "Open on this device" is still offered, because a Medicare
// client without a smartphone is a real person Stewardship serves — but
// it is the second option and it says plainly what it costs.
//
// The genuinely safe version of device-handover is serving /sign on its
// own origin (the pattern forms.stewardship.pro already uses), which
// would put the signing page in a different cookie jar entirely. That
// needs a DNS change and is Grant's call, so it is written down rather
// than assumed.

import { useCallback, useEffect, useRef, useState } from "react";
import { QRCodeCanvas } from "qrcode.react";

import { Button } from "../../ui";
import { useToast } from "../Toast";
import { apiV11, type EsignSendResponse, type EsignSignerProgress } from "../../api";

/** How often the panel re-reads the envelope while the advisor watches.
 *  Fast enough that a signature on the client's phone shows up while
 *  they are still holding it; slow enough that a forgotten open tab is
 *  not a load problem. Polling stops entirely once nobody is left. */
const POLL_MS = 4000;

interface Props {
  result: EsignSendResponse;
  onDone: () => void;
}

export function InPersonPanel({ result, onDone }: Props) {
  const toast = useToast();
  const [progress, setProgress] = useState<EsignSignerProgress[] | null>(null);
  const [pollFailed, setPollFailed] = useState(false);
  // Which signer's QR is showing. One at a time on purpose: two people
  // signing on one document each need their own link, and a wall of QR
  // codes is how the wrong person scans the wrong one.
  const [activeEmail, setActiveEmail] = useState<string | null>(
    result.signers[0]?.email ?? null,
  );
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const everyoneDone =
    progress !== null &&
    progress.length > 0 &&
    progress.every((s) => s.status === "signed" || s.status === "declined");

  const poll = useCallback(async () => {
    try {
      const res = await apiV11.getEsignEnvelope(result.envelope_id);
      setProgress(res.envelope.signers);
      setPollFailed(false);
    } catch {
      // Never an error wall: the links on screen are real whether or
      // not the status read succeeded, and the advisor can still hand
      // the phone over. Say so once and keep trying.
      setPollFailed(true);
    }
  }, [result.envelope_id]);

  useEffect(() => {
    let cancelled = false;
    async function tick() {
      if (cancelled) return;
      await poll();
      if (cancelled) return;
      timerRef.current = setTimeout(tick, POLL_MS);
    }
    void tick();
    return () => {
      cancelled = true;
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [poll]);

  // Stop polling the moment there is nothing left to watch.
  useEffect(() => {
    if (everyoneDone && timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, [everyoneDone]);

  function statusFor(email: string): EsignSignerProgress | null {
    return progress?.find((s) => s.email.toLowerCase() === email.toLowerCase()) ?? null;
  }

  const active = result.signers.find((s) => s.email === activeEmail) ?? result.signers[0] ?? null;

  return (
    <div className="esign-prep-result esign-inperson">
      <h3>Ready to sign, right here</h3>
      <p className="esign-prep-hint">
        Nobody was emailed. Have {result.signers.length === 1 ? "them" : "each person"} scan
        their own code with their phone camera — it opens the document straight
        away, with no account to create.
      </p>

      <ul className="esign-inperson-signers">
        {result.signers.map((s) => {
          const p = statusFor(s.email);
          const done = p?.status === "signed";
          const declined = p?.status === "declined";
          return (
            <li key={s.email}>
              <button
                type="button"
                className="esign-inperson-signer"
                data-active={s.email === active?.email ? "true" : undefined}
                data-done={done ? "true" : undefined}
                onClick={() => setActiveEmail(s.email)}
              >
                <span className="esign-inperson-signer-name">{s.name}</span>
                <span className="esign-inperson-signer-state">
                  {/* Null status is NOT "waiting" — it means we have heard
                      nothing yet, and saying "waiting" would claim a
                      delivery that may not have happened. */}
                  {done
                    ? "Signed ✓"
                    : declined
                      ? "Declined"
                      : p?.status === "viewed"
                        ? "Reading it"
                        : "Not signed yet"}
                </span>
              </button>
            </li>
          );
        })}
      </ul>

      {active ? (
        <div className="esign-inperson-scan">
          <QRCodeCanvas value={active.signUrl} size={192} />
          <div className="esign-inperson-scan-side">
            <p className="esign-inperson-scan-who">
              {result.signers.length > 1 ? `${active.name}'s code` : "Scan to sign"}
            </p>
            <p className="esign-prep-hint">
              Point the phone's camera at this. If scanning won't work, the link
              can be copied or opened here instead.
            </p>
            <div className="esign-inperson-scan-actions">
              <Button
                variant="sw-default"
                size="sm"
                onClick={() => {
                  void navigator.clipboard
                    ?.writeText(active.signUrl)
                    .then(() => toast.success("Link copied."))
                    .catch(() => toast.error("Couldn't copy — select the link and copy it."));
                }}
              >
                Copy link
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => window.open(active.signUrl, "_blank", "noopener,noreferrer")}
              >
                Open on this device
              </Button>
            </div>
            <p className="esign-inperson-warning">
              Opening it here hands the client a screen that is signed into
              Stewardship OS. Stay with them, and close the tab when they're
              done.
            </p>
          </div>
        </div>
      ) : null}

      {pollFailed ? (
        <p className="esign-prep-hint">
          Can't read the signing status just now — the codes above still work.
        </p>
      ) : null}

      {everyoneDone ? (
        <p className="esign-inperson-alldone">
          Everyone's finished. The signed copy files itself on this record in a
          moment.
        </p>
      ) : (
        <p className="esign-prep-hint">
          If they run out of time, close this and use <strong>Resend</strong> on
          the E-sign page to email the link instead. No reminders go out on an
          in-person send — nobody was invited by email, so a "reminder" would be
          the first they'd heard of it — and they stay off after a Resend too,
          so keep an eye on that one yourself.
        </p>
      )}

      <Button variant="primary" onClick={onDone}>
        Done
      </Button>
    </div>
  );
}
